// The bell: a peek at the latest notifications, from anywhere in the app.
//
// It was a private popover that closed only on an outside mousedown (Escape did
// nothing, and it stayed open over the page while focus walked on), a
// hand-rolled pip fed by its own count, and a list that marked everything read
// the moment it was opened, whether or not anything had been looked at (SH-06,
// SH-08, WS-06, WS-11, XA-V1). Now:
//
//  - usePopover: aria-expanded, Escape closes and returns focus to the bell,
//    focus leaving closes.
//  - The pip is the registry's count (lib/attention.ts), the same number the
//    Workspace item and the Notifications tab show.
//  - Opening reads nothing. Opening a row marks that row read; "Mark all read"
//    is a button, and a write that is refused is said in the panel.
//  - Rows name their kind (lib/notificationKinds.ts) and open where that kind
//    really lives, and "View all" goes to Workspace → Notifications.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useNotifications, type Notif } from '../lib/useNotifications';
import { kindLabel, notificationRoute } from '../lib/notificationKinds';
import { fmtRelative } from '../lib/dates';
import { canOpen } from '../lib/roles';
import { AttentionCount, Button, Icon, IconButton, TONE } from './primitives';
import { Eyebrow } from './card';
import { usePopover } from './dialog';
import { InlineStatus, LoadError } from './feedback';

export function NotificationBell({
  unread,
  onUnreadChange,
}: {
  /** The notifications queue from the attention registry. When the viewer's
   *  role has no such queue (no Workspace item to disagree with), the bell
   *  falls back to its own count. */
  unread?: number;
  /** The registry refetches on navigation only; tell it when the count moved. */
  onUnreadChange?: () => void;
}) {
  const navigate = useNavigate();
  const { session, perms } = useAuth();
  const notif = useNotifications(session?.user.id);
  const { items, loading, markAllRead, dismiss, reload } = notif;
  const pop = usePopover({ align: 'right', maxH: 420, minWidth: 340 });
  const count = unread ?? notif.unread;

  // A realtime arrival, a dismiss or a mark-read moves this hook's count; the
  // registry would not notice until the next navigation, and the sidebar, the
  // tab and the pip would sit on the old number. Not for the first load: App
  // counted on arrival already.
  const last = useRef<number | null>(null);
  useEffect(() => {
    if (loading) return;
    if (last.current !== null && last.current !== notif.unread) onUnreadChange?.();
    last.current = notif.unread;
  }, [loading, notif.unread, onUnreadChange]);

  const openItem = (n: Notif) => {
    if (!n.read_at) void notif.markRead(n.id);
    // The kind knows where a bare server link should really land
    // ('/hr' → the Requests view, '/org' → the Bug reports tab).
    const to = notificationRoute(n);
    if (!to) return;
    navigate(to);
    pop.close();
  };

  // A refused write is said here. The hook reloads either way, so the row came
  // back, or stayed unread, and that was all anyone was told. The hook's writes
  // resolve to the error's text, or to nothing once they land. Gone with the
  // panel: it is about the last thing done in it.
  const [failed, setFailed] = useState<string | null>(null);
  if (!pop.open && failed) setFailed(null);
  const write = (result: Promise<unknown>) => {
    setFailed(null);
    void result.then((r) => {
      if (typeof r === 'string') setFailed(r);
    });
  };

  // Focus moves first, then the row goes. Dismissing took the focused button
  // with it: focus fell to <body>, and Escape went dead, because the popover
  // hears it on the bell and the panel only.
  const listRef = useRef<HTMLUListElement>(null);
  const dismissItem = (i: number, id: string) => {
    const rows = listRef.current?.children;
    const next = (rows?.[i + 1] ?? rows?.[i - 1])?.querySelector<HTMLElement>('button');
    (next ?? pop.triggerRef.current)?.focus();
    write(dismiss(id));
  };

  return (
    <>
      <button
        type="button"
        {...pop.triggerProps}
        aria-haspopup="dialog"
        // The count is in the name: a button's children are presentational to
        // a screen reader, the pip included.
        aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
        title="Notifications"
        className="hf-btn"
        data-variant="secondary"
        style={{
          position: 'relative',
          width: 32,
          height: 32,
          padding: 0,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--hf-radius-md)',
        }}
      >
        <Icon name="bell" size={14} />
        <AttentionCount n={count} label="unread notifications" variant="dot" />
      </button>
      {pop.open && (
        <div
          {...pop.panelProps}
          role="dialog"
          aria-label="Notifications"
          className="hf-dialog hf-scroll"
          style={{
            ...pop.panelProps.style,
            width: 340,
            padding: 'var(--hf-space-2)',
            borderRadius: 'var(--hf-radius-card)',
            overflow: 'auto',
            color: 'var(--hf-ink)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              minHeight: 32,
              padding: '0 var(--hf-space-2)',
            }}
          >
            <Eyebrow as="h2" style={{ flex: 1 }}>
              Notifications
            </Eyebrow>
            {/* Stays while there are rows, unavailable once none is unread. It
                was removed the moment it worked, from under its own focus:
                focus fell to <body> and Escape went dead, as with Dismiss. */}
            {items.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                disabled={!items.some((n) => !n.read_at)}
                focusableWhenDisabled
                onClick={() => write(markAllRead())}
              >
                Mark all read
              </Button>
            )}
          </div>
          {failed && (
            <div style={{ padding: '0 0 var(--hf-space-2)' }}>
              <InlineStatus tone="error">Couldn’t update notifications — {failed}</InlineStatus>
            </div>
          )}

          {notif.error ? (
            // A failed read is not "No notifications" (UI-CONVENTIONS §7).
            <LoadError what="notifications" onRetry={() => void reload()} />
          ) : loading ? (
            <p style={note}>Loading…</p>
          ) : items.length === 0 ? (
            <p style={note}>No notifications.</p>
          ) : (
            <ul
              ref={listRef}
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--hf-space-1)',
              }}
            >
              {items.map((n, i) => (
                <li
                  key={n.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    borderRadius: 'var(--hf-radius-md)',
                    background: n.read_at ? 'transparent' : 'var(--hf-surface-2)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openItem(n)}
                    className="hf-btn"
                    data-variant="ghost"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'var(--hf-space-2)',
                      padding: 'var(--hf-space-2)',
                      border: 0,
                      borderRadius: 'var(--hf-radius-md)',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                    }}
                  >
                    {/* Amber, the colour of the count it adds up to. Not the
                        only sign of unread: the words below say it too. */}
                    <span
                      aria-hidden
                      style={{
                        width: 6,
                        height: 6,
                        marginTop: 6,
                        flexShrink: 0,
                        borderRadius: 'var(--hf-radius-pill)',
                        background: n.read_at ? 'transparent' : TONE.warning.fg,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {!n.read_at && <span className="hf-sr-only">Unread: </span>}
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--hf-text-sm)',
                          lineHeight: 'var(--hf-leading-snug)',
                          color: 'var(--hf-ink)',
                          fontWeight: n.read_at ? undefined : 'var(--hf-weight-medium)',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {n.body}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          marginTop: 'var(--hf-space-1)',
                          fontSize: 'var(--hf-text-xs)',
                          fontFamily: 'var(--hf-font-mono)',
                          color: 'var(--hf-ink-3)',
                        }}
                      >
                        {kindLabel(n.kind)} · {fmtRelative(n.created_at)}
                      </span>
                    </span>
                  </button>
                  <IconButton
                    icon="x"
                    label="Dismiss notification"
                    onClick={() => dismissItem(i, n.id)}
                  />
                </li>
              ))}
            </ul>
          )}

          {/* The bell holds the latest 20; the tab holds the rest, so there is
              nothing more to see when the bell is empty. Only for a role that
              can open that tab: a client has a bell and no Workspace, and the
              link would bounce off the route guard. */}
          {items.length > 0 && canOpen(perms, 'inbox') && (
            <Link
              to="/workspace?tab=notifications"
              onClick={() => pop.close(false)}
              className="hf-btn"
              data-variant="ghost"
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 'var(--hf-space-2)',
                padding: 'var(--hf-space-2)',
                border: 0,
                borderRadius: 'var(--hf-radius-md)',
                fontSize: 'var(--hf-text-sm)',
                textDecoration: 'none',
              }}
            >
              View all
            </Link>
          )}
        </div>
      )}
    </>
  );
}

const note = {
  margin: 0,
  padding: 'var(--hf-space-4) var(--hf-space-2)',
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-3)',
  textAlign: 'center',
} as const;
